import { useEffect, useState } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, LoadingState } from "@wa/ui";
import { Plug } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { aiConnectionsApi, type AiConnection } from "../../../lib/aiConnections";
import { ConnectAiApp } from "./ConnectAiApp";
import { intlLocale } from "./lib";

function fmtDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(intlLocale(locale), { dateStyle: "medium" });
  } catch {
    return "-";
  }
}

/**
 * The AI apps (Claude, ChatGPT, Codex and the like) this person allowed on the consent
 * page (`/connect`), the one way to take that back, and how to connect one
 * (`ConnectAiApp.tsx`). The consent page names this card as the place to
 * disconnect, so its title must stay "Connected apps".
 */
export function ConnectedAppsCard() {
  const t = useT();
  const { locale } = useI18n();
  const [items, setItems] = useState<AiConnection[] | null>(null);
  // The app stays set while the dialog closes, so its title does not blank out
  // during the fade.
  const [confirming, setConfirming] = useState<AiConnection | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setItems(await aiConnectionsApi.list());
    } catch {
      setItems([]);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function disconnect() {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    try {
      await aiConnectionsApi.disconnect(confirming.id);
    } catch (err) {
      // 404 = already gone (another tab, or it expired): the reload below shows that.
      if (!(err instanceof ApiError && err.status === 404)) setError(t("security.aiApps.disconnectFailed"));
    }
    await load();
    setBusy(false);
    setOpen(false);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Plug className="size-5 shrink-0 text-primary" />
          <div className="flex-1">
            <CardTitle>{t("security.aiApps.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("security.aiApps.hint")}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {items === null ? (
          <LoadingState variant="inline" label={t("common.loading")} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("security.aiApps.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((app) => (
              <li key={app.id} className="flex flex-col gap-3 rounded-md border bg-card p-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{app.appName}</div>
                    {app.returnsTo && (
                      <div className="truncate text-xs text-muted-foreground">{t("security.aiApps.returnsTo", { host: app.returnsTo })}</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {t("security.aiApps.connectedOn", { date: fmtDate(app.connectedAt, locale) })}
                      {" · "}
                      {app.lastUsedAt
                        ? t("security.aiApps.lastUsed", { date: fmtDate(app.lastUsedAt, locale) })
                        : t("security.aiApps.neverUsed")}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start sm:self-center"
                  onClick={() => {
                    setConfirming(app);
                    setOpen(true);
                  }}
                >
                  {t("security.aiApps.disconnect")}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <ConnectAiApp />
      </CardContent>

      <ConfirmDialog
        open={open}
        variant="destructive"
        title={t("security.aiApps.confirmTitle", { app: confirming?.appName ?? "" })}
        description={t("security.aiApps.confirmBody")}
        confirmLabel={t("security.aiApps.disconnect")}
        cancelLabel={t("common.cancel")}
        loading={busy}
        onConfirm={() => void disconnect()}
        onCancel={() => setOpen(false)}
      />
    </Card>
  );
}
